import type { SerializedError } from '@reduxjs/toolkit';
import type { IBaseSong, ReleaseGenre } from 'services/api/entities';
import { ReleaseSongStatus } from 'services/api/entities/distribution/release';

export enum ReleaseStatus {
  RELEASED = 'RELEASED',
  PENDING = 'PENDING',
  DRAFT = 'DRAFT',
  DECLINED = 'DECLINED',
}

export enum ReleaseFieldType {
  SongTitle = 'song_title',
  Genre = 'genre',
  CoverArt = 'cover_art',
  ArtistName = 'artist_name_id',
}

export enum ReleaseSongFieldType {
  ComposerName = 'composer',
  HasVocals = 'has_vocals',
  VocalsContent = 'vocals_content',
  VocalistName = 'vocalist',
  Language = 'language',
  AdditionalArtists = 'additional_artists',
  SongRemixers = 'remixers',
  SongProducers = 'producers',
}

export type AdvancedReleaseSongFieldType =
  | ReleaseSongFieldType.AdditionalArtists
  | ReleaseSongFieldType.SongProducers
  | ReleaseSongFieldType.SongRemixers;

export type ReleaseField = ReleaseFieldType | ReleaseSongFieldType;

export type ReleaseFieldValue = string | File;
export type ReleaseSongFieldValue = boolean | string | string[];

type ReleaseId = string;

interface ReleaseRequestLoading {
  loading: true;
  error: null;
  data: null;
}

interface ReleaseRequestSuccess {
  loading: false;
  error: null;
  data: ReleaseId;
}

interface ReleaseRequestError {
  loading: false;
  error: SerializedError;
  data: null;
}

export type ReleaseRequestState =
  | ReleaseRequestLoading
  | ReleaseRequestSuccess
  | ReleaseRequestError;

interface FieldData<T> {
  loading: boolean;
  error: SerializedError | null;
  data: T | null;
}

export type SongData = IBaseSong & {
  status: ReleaseSongStatus;
  metadata: {
    [ReleaseSongFieldType.ComposerName]: FieldData<string>;
    [ReleaseSongFieldType.HasVocals]: FieldData<boolean>;
    [ReleaseSongFieldType.VocalsContent]: FieldData<string>;
    [ReleaseSongFieldType.VocalistName]: FieldData<string>;
    [ReleaseSongFieldType.Language]: FieldData<string>;
    [ReleaseSongFieldType.AdditionalArtists]: FieldData<string[]>;
    [ReleaseSongFieldType.SongRemixers]: FieldData<string[]>;
    [ReleaseSongFieldType.SongProducers]: FieldData<string[]>;
  };
};

export interface ReleaseData {
  id: string;
  status: ReleaseStatus;
  [ReleaseFieldType.SongTitle]: FieldData<string>;
  [ReleaseFieldType.Genre]: FieldData<ReleaseGenre>;
  [ReleaseFieldType.CoverArt]: FieldData<string>;
  [ReleaseFieldType.ArtistName]: FieldData<string>;
  songs: SongData[];
}

export interface DistributionState {
  /**
   * A record [key = song id] representing the release requests that have been triggered
   */
  releaseRequests: Record<string, ReleaseRequestState>;

  /**
   * A record [key = release id] representing users releases
   */
  releases: Record<string, ReleaseData>;

  genres: ReleaseGenre[];
}
